// For options, see: https://visjs.github.io/vis-network/docs/network/nodes.html
import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  InputLabel,
  MenuItem,
  FormControl,
  FormControlLabel,
  Select,
  Switch,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { parseNode, convertBlobToBase64 } from '../lib/network/helpers';
import { setSelectedNode } from '../features/networkSlice';
import { updateNode } from '../features/networkUndoSlice';
import ImageTabs from './EditNodeDialog/ImageTabs';

export default function EditNodeDialog() {
  const dispatch = useDispatch();
  const selectedNode = useSelector((state) => state.network.selectedNode);
  const nodes = useSelector((state) => state.networkUndo.present.nodes);

  const [formData, setFormData] = useState({});
  const [idError, setIdError] = useState(null);

  useEffect(() => setFormData(selectedNode), [selectedNode]);

  const handleClose = () => {
    setIdError(null);
    dispatch((setSelectedNode(null)));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'id') { // make 'id' alphanumeric
      newValue = newValue.replace(/[^a-zA-Z0-9\-_:]/g, '');
    }
    if (name === 'physics') {
      newValue = e.target.checked;
    }
    setFormData({ ...formData, [name]: newValue });
    setIdError(null);
  };

  const handleSave = async () => {
    // parse node, e.g for adjustments based on "shape"
    const updatedNode = parseNode({ ...formData });

    // adjust if ID changed
    const nodeInState = nodes.find((item) => String(item.id) === updatedNode.id);
    if (selectedNode.id !== updatedNode.id) {
      if (nodeInState !== undefined) {
        setIdError('A node with that ID already exists');
        return;
      }
      updatedNode.oldId = selectedNode.id;
    }

    // convert local images to base64
    if (updatedNode.shape === 'image'
      && updatedNode.image
      && updatedNode.image.startsWith('blob:http')
    ) {
      updatedNode.image = await convertBlobToBase64(updatedNode.image);
    }

    // update node
    dispatch(updateNode(updatedNode));

    // reset
    dispatch(setSelectedNode(null));
  };

  return (
    <Dialog open={selectedNode !== null} onClose={handleClose} fullWidth maxWidth="md">
      {formData && (
        <form
          autoComplete="off"
          action="/"
          method="POST"
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        >
          <DialogTitle>Edit Node</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                error={!!idError}
                helperText={idError || null}
                fullWidth
                size="small"
                value={formData.id || ''}
                name="id"
                label="ID"
                onChange={handleChange}
              />
              <TextField
                fullWidth
                size="small"
                value={formData.label || ''}
                name="label"
                label="Label"
                onChange={handleChange}
                autoFocus
              />
              <TextField
                fullWidth
                size="small"
                multiline
                minRows="3"
                maxRows="10"
                value={formData.title || ''}
                name="title"
                label="Title"
                onChange={handleChange}
              />
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="shape-select">Shape</InputLabel>
                <Select
                  name="shape"
                  labelId="shape-select"
                  value={formData.shape || ''}
                  label="Shape"
                  onChange={handleChange}
                >
                  <MenuItem value="dot">Dot</MenuItem>
                  <MenuItem value="square">Square</MenuItem>
                  <MenuItem value="diamond">Diamond</MenuItem>
                  <MenuItem value="box">Box</MenuItem>
                  <MenuItem value="image">Image</MenuItem>
                </Select>
              </FormControl>
              {formData.shape === 'image' && (
                <ImageTabs formData={formData} setFormData={setFormData} />
              )}
              <FormControlLabel
                control={<Switch />}
                name="physics"
                label="Physics"
                checked={formData.physics !== undefined ? formData.physics : true}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                size="small"
                value={formData.group || ''}
                name="group"
                label="Group"
                onChange={handleChange}
              />
              <TextField
                fullWidth
                size="small"
                value={formData.color || ''}
                name="color"
                label="Color"
                onChange={handleChange}
                title="Supports 'rgba(120,32,14,1)', '#ffffff', or 'red'"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" type="submit">Save</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
